import { useEffect, useRef, useState } from 'react'
import mapboxgl from 'mapbox-gl'

const markerHeight = 20
const markerRadius = 10
const linearOffset = 25

const popupOffsets = {
  top: [0, 0],
  'top-left': [0, 0],
  'top-right': [0, 0],
  bottom: [0, -markerHeight],
  'bottom-left': [linearOffset, (markerHeight - markerRadius + linearOffset) * -1],
  'bottom-right': [-linearOffset, (markerHeight - markerRadius + linearOffset) * -1],
  left: [markerRadius, (markerHeight - markerRadius) * -1],
  right: [-markerRadius, (markerHeight - markerRadius) * -1],
}

/**
 * Hook para manejar popups de Mapbox con React Portal.
 *
 * Estrategia: el popup se REUTILIZA entre cambios de árbol.
 * Cuando el usuario selecciona otro árbol, solo se reposiciona
 * el popup existente (setLngLat). El portal actualiza el contenido
 * vía reconciliación de React, sin destruir el container del DOM.
 */
export const useMapboxPopup = ({
  map,
  lngLat,
  contentKey,
  isOpen = true,
  isMinimized = false,
  autoClose = false,
  maxWidth = '27em',
}) => {
  const popupInstanceRef = useRef(null)
  const wheelCleanupRef = useRef(null)
  const [container, setContainer] = useState(null)

  // Ref para leer isMinimized actual dentro del Effect de creación sin agregarlo a sus deps
  // (agregarlo a deps destruiría y re-crearía el popup en cada toggle de minimizado)
  const isMinimizedRef = useRef(isMinimized)
  isMinimizedRef.current = isMinimized

  useEffect(() => {
    if (!map) return

    const mapContainer = map.getContainer?.()
    if (!mapContainer || !mapContainer.isConnected) return

    // Cerrar popup cuando isOpen=false o no hay coordenadas
    if (!isOpen || !lngLat) {
      if (popupInstanceRef.current) {
        wheelCleanupRef.current?.()
        wheelCleanupRef.current = null
        popupInstanceRef.current.remove()
        popupInstanceRef.current = null
        setContainer(null)
      }
      return
    }

    // Popup ya existe → solo reposicionar (NO destruir)
    if (popupInstanceRef.current) {
      popupInstanceRef.current.setLngLat(lngLat)
      return
    }

    // Crear popup por primera vez
    const popupContainer = document.createElement('div')
    popupContainer.className = 'mapbox-popup-content-container'

    const popup = new mapboxgl.Popup({
      closeButton: autoClose,
      closeOnClick: false,
      offset: popupOffsets,
      maxWidth,
    })
      .setLngLat(lngLat)
      .setDOMContent(popupContainer)

    try {
      popup.addTo(map)
    } catch (_) {
      return
    }

    // Reenviar wheel events al canvas container para mantener scroll-zoom sobre el popup
    const canvasContainer = map.getCanvasContainer()
    const popupEl = popup.getElement()
    const forwardWheel = (e) => {
      if (!map.scrollZoom.isEnabled()) return
      e.preventDefault()
      canvasContainer.dispatchEvent(
        new WheelEvent(e.type, {
          bubbles: true,
          cancelable: true,
          deltaX: e.deltaX,
          deltaY: e.deltaY,
          deltaZ: e.deltaZ,
          deltaMode: e.deltaMode,
          clientX: e.clientX,
          clientY: e.clientY,
          ctrlKey: e.ctrlKey,
          altKey: e.altKey,
          shiftKey: e.shiftKey,
          metaKey: e.metaKey,
        })
      )
    }
    popupEl.addEventListener('wheel', forwardWheel, { passive: false })
    wheelCleanupRef.current = () => popupEl.removeEventListener('wheel', forwardWheel)

    // Aplicar visibilidad inicial ANTES de que el navegador pinte
    // Si isMinimized ya era true al crear el popup, ocultarlo de inmediato (sin flash)
    const el = popup.getElement()
    if (el && isMinimizedRef.current) {
      el.style.opacity = '0'
      el.style.visibility = 'hidden'
    }

    popupInstanceRef.current = popup
    setContainer(popupContainer)
  }, [map, lngLat, isOpen, autoClose, maxWidth])

  // Ocultar popup cuando está minimizado (con fade suave)
  useEffect(() => {
    const el = popupInstanceRef.current?.getElement()
    if (!el) return

    if (isMinimized) {
      // Fade out → luego ocultar para no bloquear clicks
      el.style.transition = 'opacity 0.2s ease'
      el.style.opacity = '0'
      const timer = setTimeout(() => {
        el.style.visibility = 'hidden'
        el.style.transition = ''
      }, 200)
      return () => clearTimeout(timer)
    } else {
      // Arrancar invisible, forzar reflow, luego fade in
      el.style.visibility = ''
      el.style.opacity = '0'
      el.style.transition = ''
      void el.offsetWidth // flush
      el.style.transition = 'opacity 0.18s ease'
      el.style.opacity = '1'
      const timer = setTimeout(() => {
        el.style.transition = ''
        el.style.opacity = ''
      }, 180)
      return () => clearTimeout(timer)
    }
  }, [isMinimized])

  // Cleanup solo al desmontar el componente
  useEffect(() => {
    return () => {
      if (popupInstanceRef.current) {
        wheelCleanupRef.current?.()
        wheelCleanupRef.current = null
        popupInstanceRef.current.remove()
        popupInstanceRef.current = null
      }
    }
  }, [])

  return container
}
